import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Users, TrendingUp, BarChart3, Eye, BookOpen, ShoppingBag, MessageCircle, ChevronDown, ChevronUp, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ==================== ASSESSMENT ANALYTICS ====================

export const AssessmentAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllResults, setShowAllResults] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics/assessments`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching assessment analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-4 text-burgundy">Loading assessment data...</div>;
  if (!data) return <div className="text-center py-4 text-burgundy/60">Unable to load assessment data.</div>;

  const pillarColors = {
    BODY: 'bg-burgundy text-cream',
    MIND: 'bg-dark-teal text-cream',
    VITALITY: 'bg-gold text-burgundy'
  };

  const totalPillars = data.pillar_distribution.BODY + data.pillar_distribution.MIND + data.pillar_distribution.VITALITY;
  const displayedResults = showAllResults ? data.individual_results : data.individual_results.slice(0, 10);

  return (
    <div className="space-y-6" data-testid="assessment-analytics">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Assessments Completed</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-burgundy">{data.total_completed}</div>
            <p className="text-xs text-burgundy/60">{data.completion_rate}% of {data.total_users} users</p>
          </CardContent>
        </Card>
        {Object.entries(data.pillar_distribution).map(([pillar, count]) => (
          <Card key={pillar}>
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">{pillar} Pillar</CardTitle></CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-burgundy">{count}</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-burgundy/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${pillar === 'BODY' ? 'bg-burgundy' : pillar === 'MIND' ? 'bg-dark-teal' : 'bg-gold'}`}
                    style={{ width: `${totalPillars > 0 ? (count / totalPillars * 100) : 0}%` }}
                  />
                </div>
                <span className="text-xs text-burgundy/60">{totalPillars > 0 ? Math.round(count / totalPillars * 100) : 0}%</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Top Symptoms */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Top Symptoms Reported</CardTitle>
          <CardDescription>Most commonly selected symptoms across all assessments — great for content planning</CardDescription>
        </CardHeader>
        <CardContent>
          {data.top_symptoms.length > 0 ? (
            <div className="space-y-2">
              {data.top_symptoms.slice(0, 15).map((item, i) => (
                <div key={`${item.category}-${item.symptom}`} className="flex items-center gap-3">
                  <span className="text-xs text-burgundy/40 w-5 text-right">{i + 1}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-burgundy font-medium">{item.symptom}</span>
                      <Badge variant="outline" className="text-[10px] border-burgundy/20">{item.category}</Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1.5 bg-burgundy/10 rounded-full overflow-hidden max-w-xs">
                        <div className="h-full bg-burgundy rounded-full" style={{ width: `${item.percentage}%` }} />
                      </div>
                      <span className="text-xs text-burgundy/60">{item.count} ({item.percentage}%)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-6">No assessment data yet</p>
          )}
        </CardContent>
      </Card>

      {/* Priority Areas Heat Map */}
      {data.priority_areas && data.priority_areas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-burgundy">Priority Areas Heat Map</CardTitle>
            <CardDescription>Which symptom categories come up most in quizzes — and how much content you have for each. Use this to plan articles and newsletters.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.priority_areas.map((area) => {
                const maxSelections = Math.max(...data.priority_areas.map(a => a.quiz_selections), 1);
                const barWidth = Math.max(5, (area.quiz_selections / maxSelections) * 100);
                const pillarColor = area.pillar === 'BODY' ? 'bg-dark-teal' : area.pillar === 'MIND' ? 'bg-[#2563EB]' : area.pillar === 'VITALITY' ? 'bg-[#D4AF37]' : 'bg-burgundy/40';
                const pillarLabel = area.pillar === 'BODY' ? 'Body' : area.pillar === 'MIND' ? 'Mind' : area.pillar === 'VITALITY' ? 'Vitality' : '';
                return (
                  <div key={area.category} className="flex items-center gap-3" data-testid={`priority-area-${area.category.toLowerCase().replace(/[\s/]+/g, '-')}`}>
                    <div className="w-40 shrink-0 text-right">
                      <span className="text-sm font-medium text-burgundy">{area.category}</span>
                      {pillarLabel && <span className="text-[10px] text-burgundy/40 ml-1.5">({pillarLabel})</span>}
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1 h-7 bg-burgundy/5 rounded overflow-hidden relative">
                        <div className={`h-full ${pillarColor} rounded transition-all`} style={{ width: `${barWidth}%` }} />
                        <span className="absolute inset-0 flex items-center pl-2 text-[11px] font-semibold text-white mix-blend-difference">
                          {area.quiz_selections} selections ({area.percentage}%)
                        </span>
                      </div>
                      <div className={`shrink-0 px-2 py-1 rounded text-xs font-semibold ${area.article_count >= 3 ? 'bg-emerald-50 text-emerald-700' : area.article_count > 0 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>
                        {area.article_count} article{area.article_count !== 1 ? 's' : ''}
                      </div>
                      {area.gap === 'needs_content' && (
                        <Badge className="bg-red-100 text-red-700 text-[10px] shrink-0">Needs Content</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-burgundy/10 flex items-center gap-4 text-xs text-burgundy/50">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-50 border border-emerald-200" /> 3+ articles</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> 1-2 articles</div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-50 border border-red-200" /> No articles</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Individual Results Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Individual Results</CardTitle>
          <CardDescription>Assessment results by user</CardDescription>
        </CardHeader>
        <CardContent>
          {data.individual_results.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-burgundy/10">
                      <th className="text-left py-2 px-3 font-medium text-burgundy/70">User</th>
                      <th className="text-left py-2 px-3 font-medium text-burgundy/70">Status</th>
                      <th className="text-left py-2 px-3 font-medium text-burgundy/70">Primary Pillar</th>
                      <th className="text-left py-2 px-3 font-medium text-burgundy/70">Top Areas</th>
                      <th className="text-left py-2 px-3 font-medium text-burgundy/70">Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map((result, i) => (
                      <tr key={result.user_email || i} className="border-b border-burgundy/5 hover:bg-burgundy/3">
                        <td className="py-2.5 px-3">
                          <div className="font-medium text-burgundy">{result.user_name}</div>
                          <div className="text-xs text-burgundy/50">{result.user_email}</div>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={
                            result.subscription_status === 'active' ? 'bg-emerald-100 text-emerald-800' :
                            result.subscription_status === 'trial' ? 'bg-blue-100 text-blue-800' :
                            'bg-purple-100 text-purple-800'
                          }>{result.subscription_status}</Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={pillarColors[result.primary_pillar] || 'bg-gray-100'}>
                            {result.primary_pillar}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex flex-wrap gap-1">
                            {result.top_subcategories?.map((sub, j) => (
                              <Badge key={j} variant="outline" className="text-[10px]">{sub}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-2.5 px-3 text-burgundy/60 text-xs">
                          {result.completed_at ? new Date(result.completed_at).toLocaleDateString() : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.individual_results.length > 10 && (
                <Button
                  variant="ghost"
                  onClick={() => setShowAllResults(!showAllResults)}
                  className="w-full mt-3 text-burgundy/60"
                  data-testid="show-all-results-btn"
                >
                  {showAllResults ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
                  {showAllResults ? 'Show Less' : `Show All ${data.individual_results.length} Results`}
                </Button>
              )}
            </>
          ) : (
            <p className="text-burgundy/60 text-center py-6">No assessments completed yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ==================== CONTENT ACTIVITY ANALYTICS ====================

export const ContentActivityAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");
  const [excludeAdmin, setExcludeAdmin] = useState(true);
  const [expandedContent, setExpandedContent] = useState(null);

  useEffect(() => {
    fetchData();
  }, [days, excludeAdmin]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/analytics/content-activity`, {
        params: { days: parseInt(days), exclude_admin: excludeAdmin }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching content activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (key) => {
    setExpandedContent(expandedContent === key ? null : key);
  };

  const ContentTable = ({ items, type, icon: Icon }) => (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-burgundy" />
            <CardTitle className="font-serif text-burgundy">
              {type === 'article' ? 'Articles' : type === 'product' ? 'Products' : 'Discussions'}
            </CardTitle>
          </div>
          <Badge variant="outline" className="text-burgundy/60">{items.length} tracked</Badge>
        </div>
      </CardHeader>
      <CardContent>
        {items.length > 0 ? (
          <div className="space-y-2">
            {items.slice(0, 10).map((item, i) => {
              const key = `${type}-${item.content_id}`;
              const isExpanded = expandedContent === key;
              return (
                <div key={key} className="border border-burgundy/10 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleExpand(key)}
                    className="w-full flex items-center justify-between p-3 hover:bg-burgundy/3 transition-colors text-left"
                    data-testid={`content-activity-${type}-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-burgundy/40 font-bold">#{i + 1}</span>
                        <span className="font-medium text-burgundy text-sm truncate">{item.content_title || 'Untitled'}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-burgundy/50">
                        <span><Eye className="inline w-3 h-3 mr-1" />{item.total_views} views</span>
                        <span><Users className="inline w-3 h-3 mr-1" />{item.unique_user_count} unique</span>
                        {item.anonymous_views > 0 && <span>{item.anonymous_views} anonymous</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-burgundy/40 shrink-0" /> : <ChevronDown className="h-4 w-4 text-burgundy/40 shrink-0" />}
                  </button>
                  {isExpanded && item.viewers && item.viewers.length > 0 && (
                    <div className="px-3 pb-3 border-t border-burgundy/5">
                      <p className="text-xs font-medium text-burgundy/60 mt-2 mb-1.5">Viewed by:</p>
                      <div className="space-y-1">
                        {item.viewers.map((viewer, vi) => (
                          <div key={vi} className="flex items-center justify-between text-xs py-1 px-2 bg-burgundy/3 rounded">
                            <div>
                              <span className="font-medium text-burgundy">{viewer.user_name}</span>
                              <span className="text-burgundy/50 ml-2">{viewer.user_email}</span>
                            </div>
                            <span className="text-burgundy/40">{new Date(viewer.viewed_at).toLocaleDateString()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-burgundy/60 text-center py-4 text-sm">No {type} views tracked yet</p>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="content-activity-analytics">
      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-burgundy/60" />
          <span className="text-sm text-burgundy/60">Time period:</span>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-32 h-8 text-sm" data-testid="activity-days-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-burgundy/60 cursor-pointer">
          <input
            type="checkbox"
            checked={excludeAdmin}
            onChange={(e) => setExcludeAdmin(e.target.checked)}
            className="rounded border-burgundy/30"
            data-testid="exclude-admin-checkbox"
          />
          Exclude my activity
        </label>
        {data && (
          <Badge variant="outline" className="text-burgundy/50">
            {data.total_views} total views in period
          </Badge>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-burgundy">Loading content activity...</div>
      ) : data ? (
        <>
          <ContentTable items={data.top_articles || []} type="article" icon={BookOpen} />
          <ContentTable items={data.top_products || []} type="product" icon={ShoppingBag} />
          <ContentTable items={data.top_discussions || []} type="discussion" icon={MessageCircle} />

          {/* Public Landing Page Views */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-burgundy" />
                <CardTitle className="font-serif text-burgundy">Public Landing Page Articles</CardTitle>
              </div>
              <CardDescription>Anonymous views from non-logged-in visitors on your blog pages</CardDescription>
            </CardHeader>
            <CardContent>
              {data.public_landing_page && data.public_landing_page.length > 0 ? (
                <div className="space-y-2">
                  {data.public_landing_page.map((item, i) => (
                    <div key={item.content_id} className="flex items-center justify-between p-3 bg-burgundy/3 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-burgundy/40 font-bold">#{i + 1}</span>
                        <span className="font-medium text-burgundy text-sm">{item.content_title}</span>
                      </div>
                      <Badge className="bg-burgundy/10 text-burgundy">{item.views} views</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-burgundy/60 text-center py-4 text-sm">No public page views tracked yet. Views will appear as visitors browse your blog pages.</p>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <p className="text-burgundy/60 text-center py-8">Unable to load content activity data.</p>
      )}
    </div>
  );
};


// ==================== QUIZ LEAD ANALYTICS ====================

export const QuizLeadAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/analytics/quiz-leads`);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching quiz lead analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div className="text-center py-4 text-burgundy">Loading quiz lead data...</div>;
  if (!data) return <div className="text-center py-4 text-burgundy/60">Unable to load quiz lead data.</div>;

  const pillarLabels = { BODY: 'Radiant Body', MIND: 'Balanced Mind', VITALITY: 'Enduring Vitality' };
  const pillarStyles = {
    BODY: 'bg-dark-teal text-cream',
    MIND: 'bg-[#002447] text-cream',
    VITALITY: 'bg-[#D4AF37] text-burgundy'
  };
  const totalPillar = Object.values(data.pillar_distribution).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="quiz-lead-analytics">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Quiz Leads</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-burgundy">{data.total_leads}</div>
            <p className="text-xs text-burgundy/60">All time captures</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Converted to Members</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{data.converted}</div>
            <p className="text-xs text-burgundy/60">{data.conversion_rate}% conversion rate</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">This Month</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-dark-teal">{data.this_month}</div>
            <p className="text-xs text-burgundy/60">New leads captured</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Unconverted</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-[#D4AF37]">{data.total_leads - data.converted}</div>
            <p className="text-xs text-burgundy/60">Potential members</p>
          </CardContent>
        </Card>
      </div>

      {/* Pillar Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Lead Pillar Distribution</CardTitle>
          <CardDescription>Which wellness pillars quiz leads are most drawn to</CardDescription>
        </CardHeader>
        <CardContent>
          {totalPillar > 0 ? (
            <div className="space-y-3">
              {Object.entries(data.pillar_distribution).map(([pillar, count]) => (
                <div key={pillar} className="flex items-center gap-3">
                  <Badge className={`${pillarStyles[pillar]} w-32 justify-center`}>{pillarLabels[pillar]}</Badge>
                  <div className="flex-1 h-3 bg-burgundy/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${pillar === 'BODY' ? 'bg-dark-teal' : pillar === 'MIND' ? 'bg-[#002447]' : 'bg-[#D4AF37]'}`}
                      style={{ width: `${totalPillar > 0 ? (count / totalPillar * 100) : 0}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-burgundy w-16 text-right">{count} ({totalPillar > 0 ? Math.round(count / totalPillar * 100) : 0}%)</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-6">No quiz leads captured yet. Leads will appear here when visitors complete the standalone quiz and subscribe.</p>
          )}
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-serif text-burgundy">Recent Quiz Leads</CardTitle>
          <CardDescription>Quiz takers who subscribed — {data.leads.length} of {data.total_leads} shown</CardDescription>
        </CardHeader>
        <CardContent>
          {data.leads.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="quiz-leads-table">
                <thead>
                  <tr className="border-b border-burgundy/10">
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Email</th>
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Name</th>
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Pillar</th>
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Top Areas</th>
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Date</th>
                    <th className="text-left py-2 px-3 font-medium text-burgundy/70">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.leads.map((lead, i) => (
                    <tr key={lead.email || i} className="border-b border-burgundy/5 hover:bg-burgundy/5 transition-colors">
                      <td className="py-2.5 px-3 text-burgundy">{lead.email}</td>
                      <td className="py-2.5 px-3 text-burgundy/70">{lead.name || '—'}</td>
                      <td className="py-2.5 px-3">
                        <Badge className={`${pillarStyles[lead.primary_pillar] || 'bg-burgundy/10 text-burgundy'} text-xs`}>
                          {pillarLabels[lead.primary_pillar] || lead.primary_pillar}
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex flex-wrap gap-1">
                          {(lead.top_areas || []).slice(0, 3).map((area, j) => (
                            <span key={j} className="text-xs bg-burgundy/5 text-burgundy/70 px-2 py-0.5 rounded-full">{area}</span>
                          ))}
                        </div>
                      </td>
                      <td className="py-2.5 px-3 text-burgundy/60 text-xs whitespace-nowrap">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2.5 px-3">
                        {lead.converted ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                            Converted {lead.subscription_status ? `(${lead.subscription_status})` : ''}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-burgundy/50 border-burgundy/20 text-xs">Lead</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-burgundy/60 text-center py-8">No quiz leads captured yet. When visitors complete the standalone wellness quiz and enter their email, they'll appear here.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
